import {
  AddEventListernerResult,
  GridInfo,
  GridList,
  GridListProps,
  GridListRef,
  Touchable,
  useEventListenersRef,
  useRefAutoUpdate,
  WithNativeHTMLProps,
} from "@kalyzee/kast-web-sdk/web-components";
import React, { ForwardedRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ReactComponent as IconChevron } from "../../assets/icons/chevron.svg";
import MeetingSessionMediaPlayer from "./MeetingSessionMediaPlayer";

import { MeetingSessionOvenMediaReceiverManagerBaseData, useOvenMediaVideoSubscriptions } from "@kalyzee/kast-web-sdk";
import { OvenMediaReceiverWebRTCSession, WebRTCSessionState } from "@kalyzee/kast-web-sdk/webrtc";
import { Meeting } from "@kalyzee/kast-web-sdk/websocket";
import { PlayerContainerRenderer, PlayerItem, PlayerRendererProvider, PlayerRendererProviderOptions } from "./interfaces";
import MeetingSessionPlayer from "./MeetingSessionPlayer";
import styles from "./PlayersGrid.module.css";
import { VideoPlayer } from "./VideoPlayer";

export const getDefaultPlayerGridItemRendererProvider = (
  meeting: Meeting,
  item?: PlayerItem,
  autoOnOffVideo?: boolean,
  defaultOptions?: PlayerRendererProviderOptions
): PlayerRendererProvider => {
  let renderProvider: PlayerRendererProvider = () => <div />;
  if (!item) return renderProvider;
  const itemData = item.getData();
  if (itemData.type === "video_session") {
    renderProvider = (renderer?: PlayerContainerRenderer, options?: PlayerRendererProviderOptions) => {
      const _options = { ...(defaultOptions ?? {}), ...(options ?? {}) };
      return (
        <MeetingSessionMediaPlayer
          style={{ width: "100%", height: "100%" }}
          meeting={meeting}
          sessionId={itemData.videoSession.data.session.id}
          mediaSessionId={itemData.videoSession.data.mediaSession?.id!}
          ovenMediaSession={itemData.videoSession}
          muted={_options.muted}
          hideSoundEffect={_options.hideSoundEffect}
          renderCustomContainer={renderer}
          autoOnOffVideo={autoOnOffVideo}
        />
      );
    };
  } else if (itemData.type === "meeting_session") {
    renderProvider = (renderer?: PlayerContainerRenderer, options?: PlayerRendererProviderOptions) => {
      const _options = { ...(defaultOptions ?? {}), ...(options ?? {}) };

      return (
        <MeetingSessionPlayer
          style={{ width: "100%", height: "100%" }}
          meeting={meeting}
          sessionId={itemData.meetingSession.id}
          ovenMediaSessions={itemData.videoSessions}
          muted={_options.muted}
          hideSoundEffect={_options.hideSoundEffect}
          renderCustomContainer={renderer}
          forceUseSession={itemData.online === false ? itemData.meetingSession : undefined} // force use session if not online
        />
      );
    };
  } else {
    renderProvider = (renderer?: PlayerContainerRenderer, options?: PlayerRendererProviderOptions) => {
      const _options = { ...(defaultOptions ?? {}), ...(options ?? {}) };
      return <VideoPlayer poster={itemData.poster} src={itemData.mediaStream} muted={_options.muted} renderCustomContainer={renderer} />;
    };
  }
  return renderProvider;
};

export interface PlayersGridEventListenerMap {
  change: (info: GridInfo<PlayerItem>) => void;
}

export interface PlayersGridProps extends WithNativeHTMLProps<HTMLDivElement> {
  name?: string;
  meeting: Meeting;
  items: PlayerItem[];
  renderItem?: (renderPlayer: PlayerRendererProvider, item: PlayerItem | undefined) => React.ReactNode;
  autoOnOffVideo?: boolean;
  callbacks?: Partial<PlayersGridEventListenerMap>;
  gridProps?: Partial<GridListProps>;
  className?: string;
  style?: React.CSSProperties;
  noPages?: boolean;
  hideButtonIfDisabled?: boolean;
  defaultRendererProviderOptions?: PlayerRendererProviderOptions;
}

export interface PlayersGridRef {
  getNativeElementRef: () => React.RefObject<HTMLDivElement>;
  getGridRef: () => React.RefObject<GridListRef>;
  getGridInfo: () => GridInfo<PlayerItem> | undefined;
  currentlyRendering: (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>) => void;
  addEventListener: <K extends keyof PlayersGridEventListenerMap>(
    key: K,
    callback: PlayersGridEventListenerMap[K]
  ) => AddEventListernerResult<PlayersGridEventListenerMap, K>;
  removeEventListener: <K extends keyof PlayersGridEventListenerMap>(key: K, callback: PlayersGridEventListenerMap[K]) => void;
}

const PlayersGridInner = (
  {
    meeting,
    items,
    renderItem,
    autoOnOffVideo = false,
    callbacks,
    gridProps,
    className,
    style,
    nativeProps,
    noPages,
    hideButtonIfDisabled,
    defaultRendererProviderOptions,
  }: PlayersGridProps,
  forwardRef: ForwardedRef<PlayersGridRef>
) => {
  const eventListenersRef = useEventListenersRef<keyof PlayersGridEventListenerMap, PlayersGridEventListenerMap>();

  const callbacksRef = useRefAutoUpdate(callbacks);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageInfo, setPageInfo] = useState<{ current: number; total: number }>();
  const gridRef = useRef<GridListRef>(null);
  const gridInfoRef = useRef<GridInfo<PlayerItem>>();
  const autoOnOffVideoRef = useRefAutoUpdate(autoOnOffVideo);
  const isDetroyedRef = useRef(false);
  const subscribptionsAPI = useOvenMediaVideoSubscriptions();

  const sessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[] = useMemo(() => {
    const result: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[] = [];
    items.forEach((item) => {
      const itemData = item.getData();
      if (itemData.type === "video_session") {
        result.push(itemData.videoSession);
      } else if (itemData.type === "meeting_session") {
        result.push(...itemData.videoSessions.filter((v) => v.data.session.id === itemData.meetingSession.id));
      }
    });
    return result;
  }, [items]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EVENTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const triggerEvent = <K extends keyof PlayersGridEventListenerMap>(key: K, ...args: Parameters<PlayersGridEventListenerMap[K]>) => {
    (callbacksRef.current?.[key] as any)?.call(null, ...args);

    // TODO : add again
    // eventListenersRef.current?.triggerEvent(key, ...args);
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------  UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const sessionIsCurrentlyRendered = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>): boolean => {
    if (!gridInfoRef.current) return false;
    for (const d of gridInfoRef.current?.data) {
      const itemData = d.item.getData();
      if (itemData.type === "video_session") {
        if (itemData.videoSession === session) return d.rendered;
      } else if (itemData.type === "meeting_session") {
        if (itemData.meetingSession.id === session.data.session.id) return d.rendered;
      }
    }
    return false;
  };

  const updateVideo = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>, forceValue?: boolean) => {
    if (!autoOnOffVideoRef.current) return;

    let shouldBeEnabled;
    if (forceValue !== undefined) shouldBeEnabled = forceValue;
    else shouldBeEnabled = sessionIsCurrentlyRendered(session);

    subscribptionsAPI.update([{ session, active: shouldBeEnabled }]);
  };

  const updateVideos = (forceValue?: boolean) => {
    sessions.forEach((s) => updateVideo(s, forceValue));
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------  EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useImperativeHandle(
    forwardRef,
    () => ({
      getNativeElementRef: () => containerRef,
      getGridRef: () => gridRef,
      getGridInfo: () => gridInfoRef.current,
      currentlyRendering: (session) => sessionIsCurrentlyRendered(session),
      addEventListener: <K extends keyof PlayersGridEventListenerMap>(key: K, callback: PlayersGridEventListenerMap[K]) =>
        eventListenersRef.current?.addEventListener(key, callback),
      removeEventListener: <K extends keyof PlayersGridEventListenerMap>(key: K, callback: PlayersGridEventListenerMap[K]) =>
        eventListenersRef.current?.removeEventListener(key, callback),
    }),
    []
  );

  useEffect(() => {
    if (!gridRef.current) return;
    const metadataListener = gridRef.current.addEventListener("metadata", (metadata) => {
      if (!gridRef.current) return;
      setPageInfo({ current: gridRef.current.getPage(), total: metadata.pages });
    });
    const pageListener = gridRef.current.addEventListener("page", (page, metadata) => {
      setPageInfo({ current: page, total: metadata.pages });
    });
    if (gridRef.current) {
      const metadata = gridRef.current.getMetadata();
      if (metadata) setPageInfo({ current: gridRef.current.getPage(), total: metadata.pages });
    }
    return () => {
      updateVideos(false); // force disable videos
      isDetroyedRef.current = true;
      metadataListener.flush();
      pageListener.flush();
    };
  }, []);

  useEffect(() => {
    const listeners: {
      session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>;
      state: (state: WebRTCSessionState) => void;
    }[] = [];
    sessions.forEach((s) => {
      const listener = {
        session: s,
        state: (state: WebRTCSessionState) => {
          if (state === WebRTCSessionState.RUNNING) updateVideo(s); // true so the state entry is created
        },
      };
      listeners.push(listener);
      s.addEventListener("state", listener.state);
    });
    return () => {
      listeners.forEach((l) => {
        l.session.removeEventListener("state", l.state);
      });
    };
  }, [sessions]);

  // ---------------------------------------------------------------------- //
  // ---------------------------  RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  const renderPageButton = (type: "previous" | "next") => {
    if (!pageInfo) return null;
    if (pageInfo.total <= 1) return;
    const disabled = type === "previous" ? pageInfo.current <= 0 : pageInfo.current >= pageInfo.total - 1;
    const currentClasses = [styles.button];
    if (disabled) currentClasses.push(styles.buttonDisabled);
    return (
      <Touchable
        className={currentClasses.join(" ")}
        disabled={disabled}
        onPress={() => {
          if (!gridRef.current) return;
          const currentPage = gridRef.current.getPage();
          gridRef.current.setPage(type === "previous" ? currentPage - 1 : currentPage + 1);
        }}
        style={{ visibility: disabled && hideButtonIfDisabled ? "hidden" : "visible" }}
      >
        {type === "previous" ? (
          <IconChevron width={20} height={20} style={{ transform: "rotate(-90deg)" }} />
        ) : (
          <IconChevron width={20} height={20} style={{ transform: "rotate(90deg)" }} />
        )}
      </Touchable>
    );
  };

  const classes = [styles.container];
  if (className) classes.push(className);
  return (
    <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style} ref={containerRef}>
      {renderPageButton("previous")}
      <GridList<PlayerItem>
        className={styles.grid}
        gap={5}
        targetContentRatio={2.5}
        resizeObserverRef={containerRef}
        {...gridProps}
        ref={gridRef}
        data={items}
        hideDefaultPagination
        callbacks={{
          change: (info) => {
            if (isDetroyedRef.current) return;
            gridInfoRef.current = info;
            updateVideos();
            triggerEvent("change", info);
            gridProps?.callbacks?.change?.(info);
          },
        }}
        noPages={noPages}
        renderItem={(_, item) => {
          const defaultRootRenderer = getDefaultPlayerGridItemRendererProvider(meeting, item, false, defaultRendererProviderOptions);
          if (renderItem) return renderItem(defaultRootRenderer, item);
          return defaultRootRenderer();
        }}
      />
      {renderPageButton("next")}
    </div>
  );
};

export const PlayersGrid = React.forwardRef(PlayersGridInner) as (
  props: PlayersGridProps & { ref?: React.ForwardedRef<PlayersGridRef> }
) => ReturnType<typeof PlayersGridInner>;
